import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  inject,
  Input,
  QueryList,
} from '@angular/core';
import { getUrlParams } from '../../../functions/get-url-params';
import { BaseComponent } from '../../base/base.component';
import { StepComponent } from './step.component';

@Component({
  selector: 'app-stepper',
  imports: [CommonModule],
  template: `
    <nav aria-label="Progress" class="tw-w-full">
      <div class="tw-flex tw-w-full tw-justify-between">
        <div
          class="tw-max-w-[100px] tw-hidden sm:tw-block sm:tw-max-w-[100px]"
          aria-hidden="true"
        ></div>
        <ol
          class="tw-flex tw-grow tw-min-w-0 tw-justify-between tw-list-none tw-m-0 tw-p-0"
        >
          @for (
            step of steps;
            track step.name;
            let last = $last;
            let first = $first;
            let index = $index
          ) {
            <li
              class="tw-flex tw-flex-col tw-items-center tw-grow tw-text-[var(--sed-dynamic-black-color)] tw-list-none"
            >
              <div class="tw-flex tw-items-center tw-w-full">
                <div class="tw-flex tw-items-center tw-w-full">
                  @if (index < currentIndex) {
                    <div
                      class="tw-grow tw-mr-2"
                      [class.border-complete]="!first"
                      role="presentation"
                    ></div>
                    <button
                      type="button"
                      class="circle complete tw-cursor-pointer"
                      (click)="select(step.name)"
                      [attr.aria-label]="
                        'Go back to ' + step.label + ' step, completed'
                      "
                    >
                      <i
                        class="icon fa-regular fa-check"
                        aria-hidden="true"
                      ></i>
                    </button>
                    <div
                      class="tw-grow tw-ml-2"
                      [class.border-complete]="!last"
                      role="presentation"
                    ></div>
                  } @else if (index === currentIndex) {
                    <div
                      class="tw-grow tw-mr-2"
                      [class.border-complete]="!first"
                      role="presentation"
                    ></div>
                    <!-- Do not make current step a button/tabindex — not interactive; aria-label is enough for AT. -->
                    <div
                      class="circle current"
                      aria-current="step"
                      [attr.aria-label]="
                        'Step ' +
                        (index + 1) +
                        ' of ' +
                        stepCount +
                        ': ' +
                        step.label
                      "
                    >
                      <i
                        [class]="step.icon"
                        class="icon fa-regular"
                        aria-hidden="true"
                      ></i>
                    </div>
                    <div
                      class="tw-grow tw-ml-2"
                      [class.border]="!last"
                      role="presentation"
                    ></div>
                  } @else {
                    <div
                      class="tw-grow tw-mr-2"
                      [class.border]="!first"
                      role="presentation"
                    ></div>
                    @if (activateAllSteps) {
                      <button
                        type="button"
                        class="circle pending tw-cursor-pointer"
                        (click)="select(step.name)"
                        [attr.aria-label]="
                          'Step ' +
                          (index + 1) +
                          ' of ' +
                          stepCount +
                          ': ' +
                          step.label +
                          '. Go to this step.'
                        "
                      >
                        <i
                          [class]="step.icon"
                          class="icon fa-light"
                          aria-hidden="true"
                        ></i>
                      </button>
                    } @else {
                      <!-- Do not make future steps focusable unless activateAllSteps (then use clickable buttons). -->
                      <div
                        class="circle pending"
                        [attr.aria-label]="
                          'Step ' +
                          (index + 1) +
                          ' of ' +
                          stepCount +
                          ': ' +
                          step.label +
                          ', not started yet'
                        "
                      >
                        <i
                          [class]="step.icon"
                          class="icon fa-light"
                          aria-hidden="true"
                        ></i>
                      </div>
                    }
                    <div
                      class="tw-grow tw-ml-2"
                      [class.border]="!last"
                      role="presentation"
                    ></div>
                  }
                </div>
              </div>
              <div
                class="tw-font-bold tw-pt-1 tw-w-[80px] sm:tw-w-[150px] tw-text-center"
              >
                {{ step.label }}
              </div>
            </li>
          }
        </ol>
        <div
          class="tw-max-w-[100px] tw-hidden sm:tw-block sm:tw-max-w-[100px]"
          aria-hidden="true"
        ></div>
      </div>
    </nav>

    @if (preserveSteps) {
      @for (entry of renderedSteps; track entry.key) {
        <div [hidden]="entry.index !== currentIndex">
          <ng-container *ngTemplateOutlet="entry.step.template"></ng-container>
        </div>
      }
    } @else {
      <ng-container *ngTemplateOutlet="current?.template"></ng-container>
    }
  `,
  styles: `
    .border {
      margin-top: 0px;
      border-top: 3px solid var(--sed-grey-40-color);
    }

    .border-complete {
      margin-top: 0px;
      border-top: 3px solid var(--sed-sedgwick-green-color);
    }

    .complete {
      background-color: var(--sed-sedgwick-green-color);
    }

    .current {
      background-color: var(--sed-dynamic-black-color);
      color: var(--sed-warm-white-color);
    }

    .pending {
      background-color: var(--sed-grey-40-color);
      color: var(--sed-black-80-color);
    }

    .circle {
      width: 32px;
      height: 32px;
      font-size: 16px;
      border-radius: 20%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    button.circle {
      border: none;
      padding: 0;
      margin: 0;
      font: inherit;
    }

    button.circle:focus {
      outline: 2px solid var(--sed-engaged-black-color);
      outline-offset: 2px;
    }

    button.circle:hover {
      opacity: 0.85;
    }
  `,
})
export class StepperComponent
  extends BaseComponent
  implements AfterContentInit
{
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  @ContentChildren(StepComponent)
  steps: QueryList<StepComponent>;

  /**
   * The current step is stored as query string parameter.
   * This is the name of the parameter.
   */
  @Input()
  stepParameter = 'step';

  @Input()
  preserveSteps = false;

  /** When true, all steps are activated and clickable (e.g. edit mode) */
  @Input()
  activateAllSteps = false;

  /**
   * Set current step by name
   */
  _selected: string;
  @Input()
  get selected(): string {
    return this._selected;
  }
  set selected(value: string) {
    this._selected = value;
    this.select(value);
  }

  protected current: StepComponent;

  /** Current step name (for parents that need step-aware UI). */
  get currentStepName(): string | undefined {
    return this.current?.name;
  }

  protected currentIndex: number;

  private activatedSteps = new Map<number, number>();
  private generation = 0;

  renderedSteps: {
    index: number;
    step: StepComponent;
    key: string;
  }[] = [];

  /** Total steps for progress labels in the template. */
  get stepCount(): number {
    return this.steps?.length ?? 0;
  }

  ngAfterContentInit(): void {
    if (this.activateAllSteps && this.steps) {
      for (let i = 0; i < this.steps.length; i++) {
        this.activateIfNew(i);
      }
    }
    if (this.selected) {
      this.select(this.selected);
    } else {
      const params = getUrlParams();
      const step = params[this.stepParameter] as string;
      if (step) {
        this.select(step);
      } else {
        this.first();
      }
    }
  }

  next() {
    if (this.currentIndex < this.steps.length - 1) {
      this.current = this.steps.toArray()[++this.currentIndex];
      if (this.preserveSteps) {
        this.activateIfNew(this.currentIndex);
      }
      this.applyStep();
    }
  }

  previous() {
    if (this.currentIndex > 0) {
      this.current = this.steps.toArray()[--this.currentIndex];
      if (this.preserveSteps) {
        this.activateIfNew(this.currentIndex);
      }
      this.applyStep();
    }
  }

  first() {
    this.current = this.steps.first;
    this.currentIndex = 0;
    if (this.preserveSteps) {
      this.activateIfNew(0);
    }
    this.applyStep();
  }

  select(name: string) {
    if (this.steps) {
      const found = this.steps.find((x) => x.name === name);
      if (found) {
        this.current = found;
        this.currentIndex = this.steps.toArray().indexOf(found);
        if (this.preserveSteps) {
          this.activateIfNew(this.currentIndex);
        }
      } else {
        this.first();
      }

      this.applyStep();
    }
  }

  resetStepsAfter(index: number) {
    let changed = false;
    for (const stepIndex of [...this.activatedSteps.keys()]) {
      if (stepIndex > index) {
        this.activatedSteps.delete(stepIndex);
        changed = true;
      }
    }
    if (changed) {
      this.rebuildRenderedSteps();
    }
  }

  private activateIfNew(index: number) {
    if (!this.activatedSteps.has(index)) {
      this.activatedSteps.set(index, ++this.generation);
      this.rebuildRenderedSteps();
    }
  }

  private rebuildRenderedSteps() {
    const stepsArray = this.steps?.toArray() || [];
    const result: {
      index: number;
      step: StepComponent;
      key: string;
    }[] = [];
    for (const [index, gen] of this.activatedSteps) {
      if (index < stepsArray.length) {
        result.push({
          index,
          step: stepsArray[index],
          key: `${index}-${gen}`,
        });
      }
    }
    result.sort((a, b) => a.index - b.index);
    this.renderedSteps = result;
  }

  private applyStep() {
    window.scrollTo(0, 0);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [this.stepParameter]: this.current.name },
      queryParamsHandling: 'merge',
    });
    this.announceCurrentStep();
  }

  private announceCurrentStep(): void {
    const label = this.current?.label;
    const total = this.stepCount;
    if (!label || total < 1) {
      return;
    }
    const msg = `Step ${this.currentIndex + 1} of ${total}: ${label}`;
    queueMicrotask(() => this.liveAnnouncer.announce(msg, 'polite'));
  }
}
